import { am5, am5xy } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { calculateLabelWidth } from './calculate-label-width';

export const createAxes = ({
  root,
  chart,
  showEndValueLabel,
  withoutValuePadding,
  dataLength,
  isLargeDataset,
  data,
  xAxisField,
}: {
  root: am5.Root;
  chart: am5xy.XYChart;
  showEndValueLabel?: boolean;
  withoutValuePadding?: boolean;
  dataLength: number;
  isLargeDataset: boolean;
  data?: types.widgets.TableData;
  xAxisField?: string;
}) => {
  const labelWidth = calculateLabelWidth({ root, data, xAxisField });
  const xAxis = chart.xAxes.push(
    am5xy.DateAxis.new(root, {
      baseInterval: { timeUnit: 'day', count: 1 },
      renderer: am5xy.AxisRendererX.new(root, {
        minorGridEnabled: true,
      }),
      groupData: true,
      maxDeviation: 0,
      groupCount: isLargeDataset ? Math.min(500, Math.floor(dataLength / 2)) : undefined,
    })
  );

  if (showEndValueLabel) {
    const updateAxisWidth = (containerWidth?: number) => {
      const currentWidth = containerWidth ?? chart.width();
      if (currentWidth > 0) {
        xAxis.set('width', currentWidth - labelWidth);
        root.resize();
      }
    };

    updateAxisWidth();

    const containerElement = root.dom;
    if (containerElement && typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const width = entry.contentRect.width;
          if (width > 0) {
            requestAnimationFrame(() => {
              updateAxisWidth(width);
            });
          }
        }
      });
      resizeObserver.observe(containerElement);

      (root.events as any).on('disposed', () => {
        resizeObserver.disconnect();
      });
    }
  }

  xAxis.get('renderer').labels.template.setAll({
    fontSize: 11,
    textAlign: 'center',
  });

  const yAxis = chart.yAxes.push(
    am5xy.ValueAxis.new(root, {
      autoZoom: false,
      renderer: am5xy.AxisRendererY.new(root, {}),
      extraMin: withoutValuePadding ? undefined : 0.1,
      extraMax: withoutValuePadding ? undefined : 0.1,
    })
  );

  yAxis.get('renderer').labels.template.setAll({
    fontSize: 11,
  });

  return { xAxis, yAxis };
};
